import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { TextField } from "@mui/material";

import { Tooltip } from "components/Tooltip";
import {
  outlinedStyles,
  inputPropStyles,
} from "components/formFields/lineItemFieldStyle";

import { formatLineDecimal } from "services/utility/list";
import { checkForUnexpectedProps } from "services/utility/misc";
import { isZero } from "services/utility/misc";

// decimal-eval is not a hugely popular library, so in case of a problem
// with this library, we could temporarily drop back to using window.Function
// (see https://developer.mozilla.org/en-US/docs/Web/JavaScript/
// Reference/Global_Objects/eval#never_use_eval!)
import { evaluate } from "decimal-eval";

const NUMERIC_EXPRESSION_MATCHER = /^[\d+\-*/ ().]*$/g;

const sx = { fontWeight: "fontWeightRegular", marginTop: 0, marginBottom: 0 };

export function LineItemNumericCalculatorField(props) {
  const {
    name,
    label,
    value: initialValue,
    toolTipText = "",
    onValueChange,
    metadata,
    error,
    readOnly,
    dataTesting,
    disabled = false,
    lineReadOnly = false,
    ...unexpected
  } = props;
  checkForUnexpectedProps("LineItemNumericCalculatorField", unexpected);

  const validNumber = useSelector(
    (state) => state.userCompanySettings.localization.validNumber
  );

  const [focused, setFocused] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [value, setValue] = useState(formatLineDecimal(initialValue, metadata));

  useEffect(() => {
    setValue(formatLineDecimal(initialValue, metadata));
  }, [initialValue, metadata]);

  function onChange(e) {
    const { value } = e.target;
    // only set value if it's a valid number...
    if (validNumber.test(value) || value.match(NUMERIC_EXPRESSION_MATCHER)) {
      setUpdated(true);
      setValue(value);
    }
  }

  function onBlur(e) {
    let { value } = e.target;
    if (!validNumber.test(value)) {
      if (value.match(NUMERIC_EXPRESSION_MATCHER)) {
        try {
          value = evaluate(value);
        } catch (SyntaxError) {
          value = "";
        }
      } else {
        value = "";
      }
    }
    setFocused(false);

    if (updated) {
      // Pass the value through formatDecimal before updating state.
      // This ensures the value is properly formatted (e.g., rounded, padded, etc.)
      onValueChange(name, formatLineDecimal(value, metadata));
      setUpdated(false);
    }
  }

  function getUiValue() {
    // added to update the input on focus to not show the default zero value
    if (focused && isZero(value) && !updated) {
      return "";
    }
    return value;
  }

  if (lineReadOnly) {
    const formattedValue = formatLineDecimal(initialValue, metadata);

    return (
      <div
        style={{
          padding: "8px 14px",
          textAlign: "right",
          fontSize: "0.8125rem",
        }}
      >
        {formattedValue}
      </div>
    );
  }

  return (
    <Tooltip title={toolTipText}>
      <TextField
        autoComplete="off"
        value={getUiValue()}
        disabled={disabled}
        variant="outlined"
        onFocus={() => setFocused(true)}
        name={name}
        label={label}
        margin="dense"
        onChange={onChange}
        onBlur={onBlur}
        fullWidth
        error={error}
        data-testing={dataTesting}
        inputProps={{
          readOnly: readOnly,
          style: {
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            textAlign: "right",
          },
        }}
        sx={error ? sx : { ...sx, ...outlinedStyles }}
        InputProps={{ sx: inputPropStyles }}
        InputLabelProps={{ shrink: true }}
      />
    </Tooltip>
  );
}
